import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';

export default function Report({ user }) {
  const navigate = useNavigate();
  const now = new Date();
  const [period, setPeriod] = useState('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await API.get('/reports', { params: { period, year, month, quarter } });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) alert('无查看报表权限');
      else alert('获取报表失败');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [period, year, month, quarter]);

  const getStatusCount = (status) => data?.tasks?.byStatus?.find(s => s.status === status)?.count || 0;
  const getDeptCount = (dept) => data?.tasks?.byDept?.find(d => d.department === dept)?.count || 0;
  const getInspCount = (type) => data?.inspections?.byType?.find(t => t.type === type)?.count || 0;
  const getSupplyDept = (dept) => data?.supplies?.byDept?.find(d => d.department === dept);

  const Card = ({ title, color, children }) => (
    <div style={{...styles.card, borderTop: `4px solid ${color}`}}>
      <h3 style={{...styles.cardTitle, color}}>{title}</h3>
      {children}
    </div>
  );

  const StatRow = ({ label, value, sub }) => (
    <div style={styles.statRow}>
      <span style={styles.statLabel}>{label}</span>
      <span style={styles.statValue}>{value}{sub && <span style={styles.statSub}> {sub}</span>}</span>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.back} onClick={() => navigate(-1)}>← 返回</button>
        <h2 style={styles.headerTitle}>工作报表</h2>
        <div style={{width:60}} />
      </div>

      {/* 筛选 */}
      <div style={styles.filterArea}>
        <div style={styles.periodBtns}>
          {[['month','月报'],['quarter','季报'],['year','年报']].map(([k,v]) => (
            <button key={k} style={{...styles.periodBtn, ...(period===k ? styles.periodBtnActive : {})}}
              onClick={() => setPeriod(k)}>{v}</button>
          ))}
        </div>
        <div style={styles.dateRow}>
          <select style={styles.dateSelect} value={year} onChange={e => setYear(parseInt(e.target.value))}>
            {[2023,2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}年</option>)}
          </select>
          {period === 'month' && (
            <select style={styles.dateSelect} value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {Array.from({length:12},(_,i)=>i+1).map(m => <option key={m} value={m}>{m}月</option>)}
            </select>
          )}
          {period === 'quarter' && (
            <select style={styles.dateSelect} value={quarter} onChange={e => setQuarter(parseInt(e.target.value))}>
              {[1,2,3,4].map(q => <option key={q} value={q}>第{q}季度</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? <div style={styles.loading}>生成报表中...</div> : data && (
        <div style={styles.content}>
          <div style={styles.reportTitle}>{data.label} 工作报表</div>

          {/* 事项统计 */}
          <Card title="📋 代办事项" color="#1a4a8a">
            <StatRow label="事项总数" value={data.tasks.total} sub="件" />
            <StatRow label="待处理" value={getStatusCount('待处理')} sub="件" />
            <StatRow label="处理中" value={getStatusCount('处理中')} sub="件" />
            <StatRow label="已完成" value={getStatusCount('已完成')} sub="件" />
            <div style={styles.divider} />
            <StatRow label="保洁部事项" value={getDeptCount('保洁部')} sub="件" />
            <StatRow label="秩序工程部事项" value={getDeptCount('秩序工程部')} sub="件" />
            <StatRow label="未分配" value={getDeptCount(null)||getDeptCount('')} sub="件" />
            {data.tasks.repairCost > 0 && <>
              <div style={styles.divider} />
              <StatRow label="预计维修费用" value={`¥${Number(data.tasks.repairCost).toFixed(2)}`} />
            </>}
          </Card>

          {/* 巡检统计 */}
          <Card title="🔍 巡检工作" color="#1a6a3a">
            <StatRow label="巡检总次数" value={data.inspections.total} sub="次" />
            <StatRow label="秩序巡逻" value={getInspCount('秩序巡逻')} sub="次" />
            <StatRow label="工程巡检" value={getInspCount('工程巡检')} sub="次" />
            <StatRow label="管家巡检" value={getInspCount('管家巡检')} sub="次" />
          </Card>

          {/* 物品采购 */}
          <Card title="📦 物品采购" color="#7b2d8b">
            <StatRow label="采购总金额" value={`¥${Number(data.supplies.total).toFixed(2)}`} />
            <StatRow label="采购总次数" value={data.supplies.count} sub="次" />
            {data.supplies.byDept?.map(d => (
              <StatRow key={d.department} label={d.department} value={`¥${Number(d.cost||0).toFixed(2)}`} sub={`${d.count}次`} />
            ))}
          </Card>

          {/* 完成率 */}
          {data.tasks.total > 0 && (
            <Card title="📊 完成情况" color="#fa8c16">
              <div style={styles.progressWrap}>
                <div style={styles.progressLabel}>
                  <span>事项完成率</span>
                  <span>{Math.round(getStatusCount('已完成') / data.tasks.total * 100)}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${Math.round(getStatusCount('已完成') / data.tasks.total * 100)}%`}} />
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '30px' },
  header: { background: 'linear-gradient(90deg, #c0392b, #e74c3c)', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  back: { background: 'transparent', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer' },
  headerTitle: { color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 },
  filterArea: { background: '#fff', padding: '12px 16px', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
  periodBtns: { display: 'flex', gap: '8px', marginBottom: '10px' },
  periodBtn: { flex: 1, padding: '8px', border: '1.5px solid #e0e0e0', borderRadius: '8px', background: '#fff', fontSize: '14px', cursor: 'pointer' },
  periodBtnActive: { background: '#c0392b', color: '#fff', borderColor: '#c0392b', fontWeight: 700 },
  dateRow: { display: 'flex', gap: '8px' },
  dateSelect: { flex: 1, padding: '8px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' },
  loading: { textAlign: 'center', padding: '60px', color: '#888' },
  content: { padding: '16px' },
  reportTitle: { fontSize: '18px', fontWeight: 700, color: '#333', textAlign: 'center', margin: '0 0 16px', padding: '12px', background: '#fff', borderRadius: '10px' },
  card: { background: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: '16px', fontWeight: 700, margin: '0 0 12px' },
  statRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f5f5f5' },
  statLabel: { fontSize: '14px', color: '#555' },
  statValue: { fontSize: '15px', fontWeight: 700, color: '#222' },
  statSub: { fontSize: '12px', color: '#888', fontWeight: 400 },
  divider: { height: '1px', background: '#e8e8e8', margin: '8px 0' },
  progressWrap: { marginTop: '4px' },
  progressLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#555', marginBottom: '6px' },
  progressBar: { height: '12px', background: '#f0f0f0', borderRadius: '6px', overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #fa8c16, #ffc53d)', borderRadius: '6px', transition: 'width 0.5s' }
};
